import React, { useEffect, useState } from 'react';
import { db } from '../../../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { X, Camera, Calendar, Image as ImageIcon } from 'lucide-react';

interface AcademyLifeItem {
    id: string;
    imageUrl: string;
    title?: string;
    desc?: string;
    caption?: string;
    createdAt: number;
}

let cachedAcademyLife: AcademyLifeItem[] | null = null;
let cachedUnsubscribe: (() => void) | null = null;
const cachedListeners = new Set<(list: AcademyLifeItem[]) => void>();

const subscribeAcademyLife = (callback: (list: AcademyLifeItem[]) => void) => {
    cachedListeners.add(callback);
    
    if (cachedAcademyLife) {
        callback(cachedAcademyLife);
    }
    
    if (!cachedUnsubscribe) {
        const q = query(collection(db, 'academy-life'), orderBy('createdAt', 'desc'));
        cachedUnsubscribe = onSnapshot(q, (snapshot) => {
            const list: AcademyLifeItem[] = [];
            snapshot.forEach((doc) => {
                list.push({ id: doc.id, ...doc.data() } as AcademyLifeItem);
            });
            cachedAcademyLife = list;
            cachedListeners.forEach(cb => cb(list));
        }, (error) => {
            console.error('Error fetching academy life:', error);
            cachedUnsubscribe = null;
        });
    }

    return () => {
        cachedListeners.delete(callback);
    };
};

const IntroAcademyLife: React.FC = () => {
    const [images, setImages] = useState<AcademyLifeItem[]>(cachedAcademyLife || []);
    const [loading, setLoading] = useState(!cachedAcademyLife);
    const [selectedImage, setSelectedImage] = useState<AcademyLifeItem | null>(null);

    useEffect(() => {
        if (cachedAcademyLife) {
            setImages(cachedAcademyLife);
            setLoading(false);
        }

        const unsubscribe = subscribeAcademyLife((list) => {
            setImages(list);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    return (
        <div className="animate-in fade-in duration-700 bg-[#0B132B] min-h-screen text-slate-100 flex flex-col">
            {/* Hero Section */}
            <section className="py-16 md:py-20 px-4 md:px-8 bg-gradient-to-br from-[#0B132B] to-[#111A36]/30 text-center shrink-0">
                <div className="max-w-5xl mx-auto space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 text-indigo-300 font-black text-[10px] uppercase tracking-wider border border-indigo-500/20">
                        <Camera size={12} /> Gallery
                    </div>
                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
                        성장의 매 순간, <br />
                        <span className="text-indigo-400">생생한 학원생활</span>
                    </h1>
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-slate-400 font-medium max-w-2xl mx-auto">
                        열정과 몰입이 가득한 정성우학원의 교육 환경과 <br />
                        학생들의 다채로운 학습 일상을 소개합니다.
                    </p>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="pb-24 px-4 md:px-8 max-w-6xl mx-auto w-full flex-1">
                {loading ? (
                    <div className="py-32 flex flex-col items-center justify-center space-y-4">
                        <div className="w-10 h-10 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                        <p className="text-slate-400 font-medium text-sm">학원생활 사진을 로딩하는 중입니다...</p>
                    </div>
                ) : images.length === 0 ? (
                    <div className="py-32 text-center rounded-[2.5rem] bg-[#111A36]/20 border border-indigo-950/30">
                        <ImageIcon size={48} className="mx-auto text-slate-600 mb-4" />
                        <p className="text-slate-300 font-black text-lg">학원생활 사진이 비어 있습니다.</p>
                        <p className="text-slate-500 text-sm font-medium mt-1">학원 관리자 페이지에서 활동사진을 등록해 보세요.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {images.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => setSelectedImage(item)}
                                className="group bg-[#111A36]/30 border border-indigo-950/40 rounded-[2rem] overflow-hidden hover:border-indigo-500/20 transition-all duration-300 shadow-lg cursor-pointer flex flex-col h-full"
                            >
                                <div className="aspect-[4/3] relative overflow-hidden bg-slate-950">
                                    <img
                                        src={item.imageUrl}
                                        alt={item.title || item.caption}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <div className="absolute inset-0 bg-indigo-950/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                                            <span className="text-white text-xs font-bold">확대</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-5 flex-1 flex flex-col justify-between gap-3 text-left">
                                    <div className="space-y-1">
                                        <p className="text-slate-200 font-black text-xs sm:text-sm md:text-base leading-snug line-clamp-1">
                                            {item.title || item.caption || '정성우학원 활동 모습'}
                                        </p>
                                        <p className="text-slate-400 font-medium text-[11px] sm:text-xs leading-normal line-clamp-2 mt-1">
                                            {item.desc || (item.title ? '' : '설명 없음')}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-slate-500 text-[10px] font-bold uppercase tracking-wider mt-2 shrink-0">
                                        <Calendar size={12} />
                                        {new Date(item.createdAt).toLocaleDateString()}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Fullscreen Photo Modal */}
            {selectedImage && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm animate-in fade-in duration-300"
                        onClick={() => setSelectedImage(null)}
                    ></div>
                    <div className="relative max-w-4xl w-full max-h-[85vh] flex flex-col items-center z-10 animate-in zoom-in-95 duration-300">
                        <button
                            onClick={() => setSelectedImage(null)}
                            className="absolute -top-12 right-0 text-white/70 hover:text-white bg-white/5 hover:bg-white/10 p-2.5 rounded-full transition-all active:scale-95 flex items-center gap-1.5 text-xs font-bold"
                        >
                            <X size={18} /> 닫기
                        </button>
                        <div className="bg-[#111A36]/40 border border-indigo-950/40 rounded-[2.5rem] overflow-hidden p-2 shadow-2xl backdrop-blur-xl w-full flex flex-col items-center">
                            <img
                                src={selectedImage.imageUrl}
                                alt={selectedImage.title || selectedImage.caption}
                                className="max-w-full max-h-[65vh] rounded-[2rem] object-contain"
                            />
                            <div className="w-full text-left px-6 py-4 space-y-1">
                                <p className="text-white font-black text-sm sm:text-base md:text-lg">
                                    {selectedImage.title || selectedImage.caption || '정성우학원 활동 모습'}
                                </p>
                                {selectedImage.desc && (
                                    <p className="text-slate-300 text-xs sm:text-sm font-medium leading-relaxed">
                                        {selectedImage.desc}
                                    </p>
                                )}
                                <p className="text-slate-500 text-[10px] font-bold mt-2 uppercase tracking-wider">
                                    등록일: {new Date(selectedImage.createdAt).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntroAcademyLife;
